/**
 * Filter-context strip for the Scheduled Dashboard.
 *
 * Renders above the sub-tabs so the context stays on screen for every period
 * view (Current Week, Per Week, Year to Date, Forecast) rather than only one.
 *
 * Shows whichever filter is active:
 *  - a part → the part, its current supplier and its unit price
 *  - a supplier → that supplier's forward position
 *  - both → both
 *
 * Renders nothing when neither is filtered: a supplier or a unit price across
 * a mixed basket of parts would be meaningless.
 *
 * @link ScheduledTab for the shell that positions it.
 */
import { Package, Factory, Tag, Boxes } from 'lucide-react';
import { useFormatters } from '../../../contexts/PreferencesContext';
import type { PartInfo, SupplierInfo } from '../../../lib/api/scheduled-dashboard';

interface PartInfoBarProps {
  info: PartInfo | null;
  supplierInfo?: SupplierInfo | null;
}

/**
 * @param props - See {@link PartInfoBarProps}
 * @returns The strip, or null when nothing is filtered.
 */
export function PartInfoBar({ info, supplierInfo }: PartInfoBarProps) {
  // Uses the formatter directly rather than the shell's narrowed
  // `(n) => string` prop: that one formats whole units, which is right for a
  // £27k pipeline total but turns a £3.62 unit price into "£4". A unit price
  // needs its own currency and 2dp.
  const { formatCurrency, formatDate, formatQuantity } = useFormatters();
  if (!info && !supplierInfo) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        {/* Supplier filter with no part: the mirror question — what is this
            supplier on the hook for. */}
        {!info && supplierInfo && (
          <>
            <Field icon={<Factory className="h-4 w-4" />} label="Supplier">
              <span className="text-base font-semibold text-slate-900">
                {supplierInfo.supplier}
              </span>
            </Field>
            <Field icon={<Boxes className="h-4 w-4" />} label="Parts with qty due">
              <span className="text-base font-semibold tabular-nums text-slate-900">
                {supplierInfo.parts.toLocaleString()}
              </span>
            </Field>
            <Field icon={<Package className="h-4 w-4" />} label="Open qty">
              <span className="text-base font-semibold tabular-nums text-slate-900">
                {formatQuantity(supplierInfo.open_qty)}
              </span>
            </Field>
            {/* No FX anywhere in this pipeline: open_value is a plain sum of
                qty x unit_price. Label it with the supplier's own currency, and
                where they trade in several, say so rather than stamping one
                symbol on a mixed total. */}
            <Field icon={<Tag className="h-4 w-4" />} label="Open value">
              {supplierInfo.currency ? (
                <span className="text-base font-semibold tabular-nums text-slate-900">
                  {formatCurrency(supplierInfo.open_value, supplierInfo.currency)}
                </span>
              ) : (
                <span
                  className="text-base font-semibold tabular-nums text-slate-900"
                  title="Orders span multiple currencies — this total is not converted"
                >
                  {Math.round(supplierInfo.open_value).toLocaleString()}
                  <span className="ml-1 text-sm font-normal text-slate-500">mixed currencies</span>
                </span>
              )}
            </Field>
          </>
        )}

        {info && (
        <>
        <Field icon={<Package className="h-4 w-4" />} label="Part">
          <span className="font-mono text-base font-semibold text-slate-900">
            {info.part_number}
          </span>
          {/* Separated rather than run on after the number — the two read as
              one string otherwise, and ERP descriptions carry their own
              punctuation and padding. */}
          {info.description && (
            <span className="ml-2 border-l border-slate-300 pl-2 text-base font-medium text-slate-900">
              {info.description.trim()}
            </span>
          )}
        </Field>

        {/* The supplier's own code — what they'd quote back on a delivery
            note. Only shown when they've given us one. */}
        {info.supplier_part_number && (
          <Field icon={<Tag className="h-4 w-4" />} label="Supplier part no">
            <span className="font-mono text-base font-semibold text-slate-900">
              {info.supplier_part_number}
            </span>
          </Field>
        )}

        <Field icon={<Factory className="h-4 w-4" />} label="Current supplier">
          {info.current_supplier ? (
            <span className="text-base font-medium text-slate-900">
              {info.current_supplier}
            </span>
          ) : (
            <span className="text-base text-slate-400">—</span>
          )}
          {/* Dual-sourced parts would otherwise read as single-sourced. The
              current supplier stays first and unqualified; alternates are
              labelled once and separated, rather than each carrying its own
              "also" prefix. */}
          {info.other_suppliers.length > 0 && (
            <span className="ml-2 text-base text-slate-900">
              <span className="text-slate-900">· also </span>
              {info.other_suppliers.join(', ')}
            </span>
          )}
        </Field>

        <Field icon={<Tag className="h-4 w-4" />} label="Unit price">
          {info.unit_price !== null ? (
            <>
              <span className="text-base font-semibold tabular-nums text-slate-900">
                {formatCurrency(info.unit_price, info.currency ?? undefined, 2)}
              </span>
              {/* Which PO the price came from, and when it was raised — a
                  years-old price on a live part is worth seeing. */}
              {info.price_source_po && (
                <span className="ml-2 text-sm text-slate-900">
                  from PO {info.price_source_po}
                  {info.price_source_date ? ` · raised ${formatDate(info.price_source_date)}` : ''}
                </span>
              )}
            </>
          ) : (
            <span className="text-base text-slate-400">—</span>
          )}
        </Field>
        </>
        )}
      </div>
    </div>
  );
}

function Field({
  icon, label, children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-900">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center">{children}</div>
    </div>
  );
}
